<template>
  <b-modal
    id="cc-product-view-modal"
    hide-header
    centered
    body-class="p-0"
    content-class="border-0 pb-0"
    footer-class="justify-content-between border-top pt-2"
    @shown="initForm"
    @hidden="resetForm">
    <template v-if="!loading.product && viewProduct">
      <div
        class="product-image bg-dark position-relative"
        :style="{ 'background': `url('${productImage}') no-repeat center / cover` }">
        <b-button
          id="cc-close-product-view-modal"
          class="position-absolute rounded-circle p-0 d-flex align-items-center justify-content-center bg-white border-0 shadow-none"
          @click="closeProductViewModal">
        </b-button>
      </div>
      <div class="product-details p-4">
        <h3 class="pl-2 pr-3"> {{ productName }} </h3>
        <div class="product-description pl-2 pr-3 mb-3" v-html="productDescription"></div>
        <div class="scrollable-content pl-2 pr-3">
          <template v-if="productType === 'variable' && viewProduct.attributes.length > 0">
            <ProductAttributesTemplate
              v-for="(attribute, attributeIndex) in productAttributeNames"
              :key="`product-addOns-${attributeIndex}`"
              :id="`product-attribute-${attributeIndex}`"
              type="variants"
              class="mb-3">
              <template slot="attributeName">{{ attribute }}</template>
              <template slot="attributeError"> {{ errors && errors.attributes ? errors.attributes[attribute] : '' }} </template>
              <template
                slot="variations"
                v-for="(variation, variationIndex) in trimmedAttributes[attribute]">
                <b-col md="12" :key="`variation-${variationIndex}-name`">
                  <b-form-radio
                    :name="attribute"
                    :id="`${variation.attribute}-${variation.value}-${variationIndex}`"
                    v-model="form.attributes[attribute]"
                    :value="variation">
                    {{ variation.value }}
                  </b-form-radio>
                </b-col>
              </template>
            </ProductAttributesTemplate>
          </template>
          <template v-if="isAddOns">
            <ProductAttributesTemplate
              v-for="(addOnCategory, addOnCategoryIndex) in formattedProductAddOnCategories"
              :key="`product-addOnCategory-${addOnCategoryIndex}`"
              :id="`product-addOnCategory-${addOnCategoryIndex}`"
              type="addons"
              :limit="addOnCategory.limit"
              :count="form.add_ons[addOnCategory.uuid] ? form.add_ons[addOnCategory.uuid].length : 0"
              class="mb-3">
              <template slot="attributeName">{{ addOnCategory.name }}</template>
              <template
                slot="variations"
                v-for="(addOns, addOnsIndex) in addOnCategory.add_ons">
                <b-col md="12" :key="`addOns-${addOnsIndex}-name`" class="d-flex justify-content-between">
                  <div>
                    <b-form-checkbox
                      :name="addOns.name"
                      :id="addOns.uuid"
                      v-model="form.add_ons[addOnCategory.uuid]"
                      :value="addOns.uuid"
                      @input="checkSelectedAddOn(addOnCategory.uuid)">
                      {{ addOns.name }}
                    </b-form-checkbox>
                  </div>
                  <div class="text-right d-inline-flex align-items-center text-muted" v-if="!addOns.is_free">
                    + &#8369; {{ addOns.price | currency }}
                  </div>
                </b-col>
              </template>
            </ProductAttributesTemplate>
          </template>
        </div>
      </div>
      <template slot="modal-footer">
        <div class="item-quantity d-flex align-items-center">
          <b-button
            id="product-minus"
            class="item-quantity-btn mr-2 p-0 rounded-circle d-inline-flex align-items-center justify-content-center"
            :disabled="viewProduct.quantity === 1"
            @click="updateQuantity('subtract')">
            -
          </b-button>
          <span class="form-control mx-2 border-0 text-center">{{ viewProduct.quantity }}</span>
          <b-button
            id="product-plus"
            class="item-quantity-btn ml-2 p-0 rounded-circle d-inline-flex align-items-center justify-content-center"
            :disabled="isAddQuantity"
            @click="updateQuantity('plus')">
            +
          </b-button>
        </div>
        <b-button variant="primary" @click="addToCart">
          Add to Cart - &#8369; {{ computedAmount | currency }}
        </b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";

import ProductAttributesTemplate from "./ProductAttributesTemplate";

export default {
  components: { ProductAttributesTemplate },
  data() {
    return {
      form: {
        attributes: {},
        add_ons: {}
      },
      trimmedAttributes: {}
    }
  },
  computed: {
    ...mapGetters({
      viewProduct: "conversationalCommerce/viewProduct",
      errors: "conversationalCommerce/errors",
      loading: "conversationalCommerce/loading"
    }),
    productType: {
      get() {
        return this.viewProduct
          ? this.viewProduct.type
            ? this.viewProduct.type
            : this.viewProduct.product_details.type
          : null;
      },
      set(type) {
        return type;
      }
    },
    productImage: {
      get() {
        const hasImages = (type) => {
          if (type === 'simple') return this.viewProduct.product_details.images.length > 0;
          else if (type === 'variable') return this.viewProduct.images.length > 0;
          else return false;
        };

        const displayImage = (type) => {
          if (type === 'simple' && hasImages(type)) {
            return this.viewProduct.product_details.images[0].image_name;
          } else if (type === 'variable' && hasImages(type)) {
            return this.viewProduct.images[0].image_name;
          }
        };

        if (this.viewProduct && hasImages(this.productType)) return displayImage(this.productType);
        else return;
      },
      set(image) {
        return image;
      }
    },
    productName: {
      get() {
        if (this.viewProduct && this.productType === 'simple') return this.viewProduct.product_details.name;
        else if (this.viewProduct && this.productType === 'variable') return this.viewProduct.name;
        else return;
      },
      set(name) {
        return name;
      }
    },
    productDescription: {
      get() {
        if (this.viewProduct && this.productType === 'simple') return this.viewProduct.product_details.description;
        else if (this.viewProduct && this.productType === 'variable') return this.viewProduct.description;
        else return;
      },
      set(description) {
        return description;
      }
    },
    productAddOnCategories: {
      get() {
          if (this.viewProduct && this.productType === 'simple') return this.viewProduct.product_details.add_on_category;
          else if (this.viewProduct && this.productType === 'variable') return this.viewProduct.add_on_category;
          else return;
      },
      set(categories) {
        return categories;
      }
    },
    formattedProductAddOnCategories: {
      get() {
        if (this.productAddOnCategories.length > 0) return this.productAddOnCategories.filter(category => category.add_ons.length > 0);
        else return [];
      },
      set(addOnCategories) {
        return addOnCategories;
      }
    },
    isAddOns: {
      get() {
        if (this.viewProduct && this.productType === 'simple') return this.viewProduct.product_details.add_on_category.length > 0;
        else if (this.viewProduct && this.productType === 'variable') return this.viewProduct.add_on_category.length > 0;
        else false;
      },
      set(status) {
        return status;
      }
    },
    isAddQuantity: {
      get() {
        const isVariableQuantity = () => {
          let isDisabled = false;
          const formAttributeValues = Object.values(this.form.attributes);
          const inventoryAttributes = this.viewProduct.inventory.find(item => JSON.stringify(item.decoded_variation_code) === JSON.stringify(formAttributeValues));

          const stock = this.viewProduct && inventoryAttributes
            ? inventoryAttributes.stock
            : 0;

          isDisabled = inventoryAttributes && inventoryAttributes.is_limited_stock
            ? this.viewProduct.quantity >= stock
            : false;
          return isDisabled;
        }

        return this.productType === 'simple' && this.viewProduct.is_limited_stock
          ? this.viewProduct.quantity >= this.viewProduct.stock
          : this.productType === 'variable'
            ? isVariableQuantity()
            : false;
      },
      set(status) {
        return status;
      }
    },
    computedAmount: {
      get() {
        const computedAddOns = () => {
          let addOnsPrice = 0;
          Object.keys(this.form.add_ons).forEach(addOnCategory => {
            if (this.form.add_ons[addOnCategory] && this.form.add_ons[addOnCategory].length > 0) {
              this.form.add_ons[addOnCategory].map(addOnUuid => {
                let addOnPrice = this.productType === 'simple'
                  ? this.viewProduct.product_details.add_on_category
                    .find(category => category.uuid === addOnCategory).add_ons
                    .find(addOn => addOn.uuid === addOnUuid).price
                  : this.productType === 'variable'
                    ? this.viewProduct.add_on_category
                    .find(category => category.uuid === addOnCategory).add_ons
                    .find(addOn => addOn.uuid === addOnUuid).price
                    : 0;

                if (this.productType === 'simple') {
                  let thisAddOn = this.viewProduct.product_details.add_on_category
                    .find(category => category.uuid === addOnCategory).add_ons
                    .find(addOn => addOn.uuid === addOnUuid);

                  if (thisAddOn.is_free == 1) {
                    addOnPrice = 0;
                  }  
                } else if (this.productType === 'variable') {
                  let thisAddOn = this.viewProduct.add_on_category
                    .find(category => category.uuid === addOnCategory).add_ons
                    .find(addOn => addOn.uuid === addOnUuid);
                    
                  if (thisAddOn.is_free == 1) {
                    addOnPrice = 0;
                  }  
                }
                
                addOnsPrice = addOnsPrice += addOnPrice;
              });
            }
          });
          return addOnsPrice * this.form.quantity;
        };

        const computedVariableProduct = () => {
          let attributesPrice = 0;
          const formAttributeValues = Object.values(this.form.attributes);
          const inventoryAttributes = this.viewProduct.inventory.find(item => JSON.stringify(item.decoded_variation_code) === JSON.stringify(formAttributeValues));

          const price = this.viewProduct && inventoryAttributes
            ? inventoryAttributes.regular_price
            : 0;

          attributesPrice = attributesPrice += price;
          return attributesPrice * this.form.quantity;
        };

        if (this.viewProduct && this.productType === 'simple') return (this.viewProduct.quantity * Number(this.viewProduct.regular_price)) + computedAddOns();
        else if (this.viewProduct && this.productType === 'variable') return computedVariableProduct() + computedAddOns();
        else return 0;
      },
      set(amount) {
        return amount;
      }
    },
    productAttributeNames: {
      get() {
        return Object.keys(this.trimmedAttributes);
      },
      set(names) {
        return names;
      }
    }
  },
  filters: {
    currency(value) {
      if (!value) return 0;

      return Number(parseFloat(value).toFixed(2)).toLocaleString("en", {
        minimumFractionDigits: 2,
      });
    }
  },
  methods: {
    closeProductViewModal() {
      this.$root.$emit("bv::hide::modal", "cc-product-view-modal");
    },
    updateQuantity(method) {
      this.$store.commit("conversationalCommerce/setProductQuantity", { method: method });
    },
    initForm() {
      const product = this.viewProduct;
      const attributes = {};
      const addOns = {};
      if (this.productType === 'variable' && product.attributes.length > 0) {
        product.attributes.map(attribute => {
          attributes[attribute.name] = null
        });
      }
      if (this.productType === 'simple' && product.product_details.add_on_category.length > 0) {
        product.product_details.add_on_category.map(addOnCategory => {
          addOns[addOnCategory.uuid] = [];
        });
      } else if (this.productType === 'variable' && product.add_on_category.length > 0) {
        product.add_on_category.map(addOnCategory => {
          addOns[addOnCategory.uuid] = [];
        });
      }
      this.form = {
      //   uuid: product.uuid,
        quantity: product.quantity,
        attributes: attributes,
        add_ons: addOns
      }

      if (this.productType === 'variable') this.trimProductAttributes();
    },
    checkSelectedAddOn(addOnCategory) {
      const limit = this.productType === 'simple'
        ? this.viewProduct.product_details.add_on_category.find(category => category.uuid === addOnCategory).limit + 1
        : this.viewProduct.add_on_category.find(category => category.uuid === addOnCategory).limit + 1;

      const selectedAddOn = this.form.add_ons[addOnCategory] ? this.form.add_ons[addOnCategory] : [];

      this.$nextTick(() => {
        if (limit === selectedAddOn.length) this.form.add_ons[addOnCategory].splice(0, 1);
      });
    },
    async addToCart() {
      const errors = {
        attributes: {}
      };
      if (this.productType === 'variable') {
        Object.keys(this.form.attributes).forEach(formAttribute => {
          if (!this.form.attributes[formAttribute]) {
            errors.attributes[formAttribute] = `Please choose ${formAttribute}`;
          }
        });
      }
      if (!_.isEmpty(errors.attributes)) this.$store.commit("conversationalCommerce/setErrors", errors);
      else {
        const payload = {
          quantity: this.viewProduct.quantity,
          add_ons_uuid: Object.values(this.form.add_ons).flat(),
          remarks: ""
        };
        if (this.productType === 'variable') {
          const collectAttributes = JSON.stringify(Object.values(this.form.attributes));
          payload['product_inventory_uuid'] = this.viewProduct.inventory.find(item => JSON.stringify(item.decoded_variation_code) === collectAttributes).uuid;
        } else if (this.productType === 'simple') {
          payload['product_inventory_uuid'] = this.viewProduct.uuid;
        }
        this.$store.commit("conversationalCommerce/setErrors", null);
        await this.$store.dispatch("conversationalCommerce/addItemToCart", payload);
        if (_.isEmpty(this.errors)) this.$root.$emit("bv::hide::modal", "cc-product-view-modal");
      }
    },
    resetForm() {
      this.form = {
        attributes: {},
        add_ons: {}
      };
      this.$store.commit("conversationalCommerce/setViewProduct", null)
    },
    trimProductAttributes() {
      const collectAttributes = {};
      let inventoryAttributes = {};
      this.viewProduct.attributes.map(attribute => {
        collectAttributes[attribute.name] = attribute.variations;
      });
      inventoryAttributes = _.groupBy(_.uniqBy(
        this.viewProduct.inventory
        .map(item => item.decoded_variation_code)
        .map(variations => variations)
        .flat(),
        'value'
      ), 'attribute');

      this.trimmedAttributes = inventoryAttributes;
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.$store.commit("conversationalCommerce/setProductQuantity", { method: 'reset' });
      }
    }
  }
}
</script>

<style lang="scss">
#cc-product-view-modal {
  .close {
    font-family: MainFontRegular, sans-serif;
  }
  .modal-content {
    border-radius: 8px !important;
    box-shadow: 0px 0px 5px rgba(131, 121, 121, 0.35);
  }
  .custom-radio .custom-control-input:checked~.custom-control-label:after {
    background: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='-4 -4 8 8'%3E%3Ccircle r='3' fill='%23fff'/%3E%3C/svg%3E") no-repeat center top 2px;
  }
  .custom-checkbox .custom-control-label::before,
  .custom-control-label::after {
    top: 4px !important;
  }
  .custom-control-label {
    word-break: break-all;
  }
}
</style>

<style lang="scss" scoped>
* {
  font-family: MainFontRegular, sans-serif;
}
#cc-product-view-modal {
  .product-image {
    height: 218px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  #cc-close-product-view-modal {
    font-family: MainFontRegular, sans-serif;
    width: 38px;
    height: 38px;
    color: #2F3941;
    background: url('~@/assets/images/icons/close.svg') no-repeat center;
    top: 11px;
    left: 15px;
  }
  .product-details {
    h3 {
      font-size: 24px;
      color: #2F3941;
    }
    .scrollable-content {
      overflow-y: auto;
      max-height: calc(100vh - 450px);
    }
    .product-description {
      font-size: 14px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: none;
    }
  }
  .modal-footer {    
    .item-quantity {
      .form-control {
        width: 43px;
        background: #E9EBED;
        border-radius: 10px;
        font-size: 14px;
      }
      .item-quantity-btn {
        width: 20px;
        height: 20px;
        background: #2F3941;
        min-width: inherit;
      }
    }
  }
}
</style>
